/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `calendarPdfExport` for the Caintra Connect frontend.
 * @module utils/appointments/calendarPdfExport
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const CALENDAR_CONTAINER_SELECTOR = ".calendar-container";
const PDF_RENDER_DELAY_MS = 300;

const formatPrintedTime = (date) =>
  date
    .toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/^(\d{2}):(\d{2})\s*(AM|PM)$/, (_match, hour, minute, period) => {
      const hour24 =
        period === "AM" && hour === "12"
          ? "00"
          : period === "AM"
            ? hour
            : period === "PM" && hour === "12"
              ? "12"
              : String(parseInt(hour, 10) + 12).padStart(2, "0");
      return `${hour24}:${minute} ${period}`;
    });

const captureCalendarCanvas = async (calendarContainer) => {
  const clone = calendarContainer.cloneNode(true);
  const hiddenWrapper = document.createElement("div");
  hiddenWrapper.style.position = "fixed";
  hiddenWrapper.style.left = "-9999px";
  hiddenWrapper.style.top = "0";
  hiddenWrapper.style.width = `${calendarContainer.offsetWidth}px`;
  hiddenWrapper.style.height = `${calendarContainer.offsetHeight}px`;
  hiddenWrapper.style.background = "#fff";
  hiddenWrapper.appendChild(clone);
  document.body.appendChild(hiddenWrapper);

  await new Promise((resolve) => setTimeout(resolve, PDF_RENDER_DELAY_MS));

  const canvas = await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
  });

  document.body.removeChild(hiddenWrapper);
  return canvas;
};

const addCenteredPdfText = (pdf, text, y, fontSize, fontStyle = "normal") => {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  pdf.setFontSize(fontSize);
  pdf.setFont("helvetica", fontStyle);
  pdf.text(text, pdfWidth / 2, y, { align: "center" });
};

const resolveCompanyHeader = ({
  userData,
  selectedBuyerCompany,
  participatingCompanyData,
  t,
}) => {
  if (userData?.tipoDeUsuario === "5" && selectedBuyerCompany) {
    return `${t("pages.viewAppointments.company")}: ${
      selectedBuyerCompany.label || t("common.unknownCompany")
    }`;
  }

  if (
    (userData?.tipoDeUsuario === "11" || userData?.compradorActivo) &&
    userData?.idEmpresa
  ) {
    const companyName =
      participatingCompanyData?.razonSocial ||
      userData?.empresaNombre ||
      t("common.unknownCompany");
    return `${t("pages.viewAppointments.company")}: ${companyName}`;
  }

  return null;
};

const appendPdfHeaderLines = (pdf, lines, startY) => {
  let currentY = startY;
  lines.filter(Boolean).forEach((line) => {
    addCenteredPdfText(pdf, line, currentY, 10, "bold");
    currentY += 6;
  });
  return currentY;
};

/**
 * Captures the appointments calendar and downloads it as a PDF.
 * @param {Object} params
 * @param {Object} params.eventData - Current event metadata
 * @param {Object} params.userData - Current user metadata
 * @param {Object|null} params.selectedBuyerCompany - Selected buyer company filter
 * @param {Object|null} params.selectedBuyerExecutive - Selected buyer executive filter
 * @param {Object|null} params.selectedRequirement - Selected requirement filter
 * @param {Object|null} params.participatingCompanyData - Participating company details
 * @param {Function} params.t - i18n translate function
 * @returns {Promise<boolean>} True when a PDF was generated
 */
export const exportCalendarToPdf = async ({
  eventData,
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
  selectedRequirement,
  participatingCompanyData,
  t,
}) => {
  const calendarContainer = document.querySelector(CALENDAR_CONTAINER_SELECTOR);
  if (!calendarContainer) {
    return false;
  }

  const canvas = await captureCalendarCanvas(calendarContainer);
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const bottomMargin = 50;
  const ratio = Math.min(
    (pdfWidth - margin * 2) / canvas.width,
    (pdfHeight - margin * 2 - bottomMargin) / canvas.height,
  );
  const finalWidth = canvas.width * ratio;
  const finalHeight = canvas.height * ratio;
  const x = (pdfWidth - finalWidth) / 2;

  addCenteredPdfText(
    pdf,
    eventData?.nombre || t("common.calendarDefaultTitle"),
    15,
    18,
    "bold",
  );

  const now = new Date();
  const subtitle = `${t("pages.viewAppointments.datePrinted")} ${now.toLocaleDateString("en-GB")} - ${formatPrintedTime(now)}`;
  addCenteredPdfText(pdf, subtitle, 25, 12);

  const headerLines = [
    resolveCompanyHeader({
      userData,
      selectedBuyerCompany,
      participatingCompanyData,
      t,
    }),
    userData?.tipoDeUsuario === "5" && selectedBuyerExecutive
      ? `${t("pages.viewAppointments.buyer")}: ${selectedBuyerExecutive.label}`
      : null,
    selectedRequirement
      ? `${t("pages.viewAppointments.requirement")}: ${
          selectedRequirement.label || t("common.unnamedRequirement")
        }`
      : null,
    userData?.tipoDeUsuario === "11" || userData?.compradorActivo
      ? `${t("pages.viewAppointments.requirementUser")}: ${
          userData.nombre || t("common.unknownUser")
        }`
      : null,
  ];

  const currentY = appendPdfHeaderLines(pdf, headerLines, 35);
  pdf.addImage(imgData, "PNG", x, currentY + 5, finalWidth, finalHeight);

  const fileName = `${eventData?.nombre || t("common.calendarDefaultTitle")}_${
    new Date().toISOString().split("T")[0]
  }.pdf`;
  pdf.save(fileName);
  return true;
};
